import { useCallback, useMemo } from 'react';
import {
  dispatch,
  getSelectedBeatPlanIds,
  getSelectedTeamMemberIds,
  getWorkspaceId,
  RouteScheduleResponse,
  updateSelectedBeatPlanIds,
  useAppSelector,
  useLazyGetRouteScheduleQuery,
  getExtraDetails,
} from '@zonofi/zono-money-store';
import { useDebounceSearch, usePaginatedList } from '../../hooks';

interface BeatPlanRouteData {
  id: string;
  name: string;
  numberOfCustomers?: number;
  beatPlanId?: string;
}

export const useDigitalOrderBeatPlan = () => {
  const workspaceId = useAppSelector(getWorkspaceId);
  const selectedTeamMemberIds = useAppSelector(getSelectedTeamMemberIds);
  const selectedBeatPlanIds = useAppSelector(getSelectedBeatPlanIds);
  const selectedTeamMemberId = selectedTeamMemberIds?.[0] ?? '';
  const extraDetails = useAppSelector(getExtraDetails);

  const [getRouteSchedule, getRouteScheduleResponse] =
    useLazyGetRouteScheduleQuery();

  const { debouncedSearchValue } = useDebounceSearch();

  const fetchBeatPlanList = useCallback(
    (params: { pageNo: number; pageSize: number }) => {
      if (!workspaceId || !selectedTeamMemberId) return;

      getRouteSchedule({
        sellerWorkspaceId: workspaceId,
        teamMemberId: selectedTeamMemberId,
        pageNo: params.pageNo,
        pageSize: params.pageSize,
        ...(debouncedSearchValue ? { debouncedSearchValue } : {}),
      });
    },
    [workspaceId, selectedTeamMemberId, debouncedSearchValue]
  );

  const {
    data: routeData,
    pageNo,
    lastItemRef,
    onScroll,
    isPageLoading,
    isEndOfList,
    refresh,
  } = usePaginatedList<RouteScheduleResponse>({
    fetchDataApiCall: fetchBeatPlanList,
    apiResponseData: getRouteScheduleResponse?.data ?? [],
    isLoading: getRouteScheduleResponse.isLoading,
    isError: getRouteScheduleResponse.isError,
    uniqueKey: 'routeId',
    dependencies: [selectedTeamMemberId, debouncedSearchValue],
    pageSize: 20,
  });

  const beatPlanRoutes = useMemo<BeatPlanRouteData[]>(
    () =>
      routeData.map((item) => ({
        id: item.routeId,
        name: item.route?.name ?? 'N/A',
        numberOfCustomers: item.route?.customerCount,
        beatPlanId: item.beatPlanId,
      })),
    [routeData]
  );

  const onBeatPlanSelect = (routeId: string) => {
    if (!routeId) return;

    const isAlreadySelected = selectedBeatPlanIds?.[0] === routeId;
    dispatch(updateSelectedBeatPlanIds(isAlreadySelected ? [] : [routeId]));
  };

  return {
    beatPlanRoutes,
    onBeatPlanSelect,
    extraDetails,
    pageNo,
    lastItemRef,
    onScroll,
    isPageLoading,
    isEndOfList,
    refresh,
    isLoading:
      pageNo === 1 &&
      (getRouteScheduleResponse.isUninitialized ||
        getRouteScheduleResponse.isFetching),
  };
};
